import './MediaBrowserFullscreenModal.less';

// eslint-disable-next-line wandb/no-deprecated-imports
import {Content, Portal, Root} from '@wandb/weave/components/Dialog/Dialog';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import * as S from '../../ImageCard/ImageCard.styles';
import {useSingleRunLink} from '../../MultiRunWorkspace/SingleRunMode';
import {TileMediaLoader} from '../TileMediaLoader';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';
import {MediaBrowserModalFooter} from './MediaBrowserModalFooter';
import {MediaBrowserModalHeader} from './MediaBrowserModalHeader';
import {MediaBrowserModalProps} from './types';
import {useModalKeyboardHandling} from './useModalKeyboardHandling';

export const MediaBrowserFullscreenModal: React.FC<MediaBrowserModalProps> = ({
  isOpen,
  modalData,
}) => {
  const {currentTileMediaSpec, closeModal} = useMediaBrowserModalContext();
  const {handleModalKeyDown} = useModalKeyboardHandling();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [availableDimensions, setAvailableDimensions] = useState({
    width: 400,
    height: 300,
  });

  const calculateAvailableDimensions = useCallback(() => {
    if (modalContentRef.current && modalData) {
      const {width: availableWidth, height: availableHeight} =
        modalContentRef.current.getBoundingClientRect();

      const adjustedWidth = Math.max(availableWidth, 400);
      const adjustedHeight = Math.max(availableHeight, 300);

      // Calculate dimensions that maintain aspect ratio and fit within available space
      const calculateFittedDimensions = (
        targetWidth: number,
        targetHeight: number
      ) => {
        const aspectRatio = targetWidth / targetHeight;
        const maxWidth = adjustedWidth;
        const maxHeight = adjustedHeight;

        let finalWidth = maxWidth;
        let finalHeight = maxHeight;

        // If width is the limiting factor (width-constrained height fits within max height)
        if (maxWidth / aspectRatio <= maxHeight) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / aspectRatio;
        } else {
          // If height is the limiting factor (height-constrained width fits within max width)
          finalWidth = maxHeight * aspectRatio;
          finalHeight = maxHeight;
        }

        return {
          width: Math.round(finalWidth),
          height: Math.round(finalHeight),
        };
      };

      if (modalData.imageDimensions) {
        const {width: imgWidth, height: imgHeight} = modalData.imageDimensions;
        const fittedDimensions = calculateFittedDimensions(imgWidth, imgHeight);
        setAvailableDimensions(fittedDimensions);
      } else {
        const fittedDimensions = calculateFittedDimensions(400, 300);
        setAvailableDimensions(fittedDimensions);
      }
    }
  }, [modalData]);

  useEffect(() => {
    if (isOpen) {
      calculateAvailableDimensions();
      const handleResize = () => {
        calculateAvailableDimensions();
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
    return () => {};
  }, [isOpen, calculateAvailableDimensions]);

  useEffect(() => {
    if (isOpen) {
      // Use capture phase to intercept events before browser shortcuts
      document.addEventListener('keydown', handleModalKeyDown, true);
    }
    return () => {
      document.removeEventListener('keydown', handleModalKeyDown, true);
    };
  }, [isOpen, handleModalKeyDown]);

  const tileMediaLoaderKey = useMemo(() => {
    if (!currentTileMediaSpec) {
      return null;
    }
    const key = `${currentTileMediaSpec.runSignature.entityName}-${currentTileMediaSpec.runSignature.projectName}-${currentTileMediaSpec.runSignature.runName}-${currentTileMediaSpec.mediaKey}-${currentTileMediaSpec.mediaStep}-${currentTileMediaSpec.mediaIndex}`;
    return key;
  }, [currentTileMediaSpec]);

  // avoiding passing object literals as props to TileMediaLoader
  const stableControls = useMemo(() => ({}), []);
  const stableConfig = useMemo(() => ({}), []);

  const handleClose = useCallback(() => {
    // The modal will handle applying pending changes internally
    closeModal();
  }, [closeModal]);

  const titleLinkToRun = useSingleRunLink(modalData?.runSignature);

  if (!isOpen || !modalData || !currentTileMediaSpec) {
    return null;
  }

  const {labels, fullScreenTitle} = modalData;

  return (
    <Root open={isOpen} onOpenChange={handleClose}>
      <Portal>
        <Content
          className="z-[2000] m-0 flex h-full w-full flex-col flex-1 p-0 min-w-0 max-w-full max-h-full focus:outline-none"
          data-test="media-modal-root">
          <Tailwind className="m-0 flex h-full max-h-full flex-col flex-1 p-0">
            <div
              className="media-modal-header flex flex-0 flex-col z-10"
              data-test="media-modal-header">
              <MediaBrowserModalHeader
                onClose={handleClose}
                tileMediaSpec={modalData.tileMediaSpec}
                fullScreenTitle={fullScreenTitle}
                titleLinkToRun={titleLinkToRun}
                labels={labels}
              />
            </div>

            <div
              ref={modalContentRef}
              className="media-modal-content flex flex-1 flex-col min-h-0"
              data-test="media-modal-content">
              <S.FullscreenWrapper className="flex flex-1 items-center justify-center min-h-0 overflow-auto">
                <S.FullscreenImageContainer className="night-aware flex items-center justify-center z-0">
                  <TileMediaLoader
                    key={tileMediaLoaderKey}
                    tileMediaSpec={currentTileMediaSpec}
                    attemptNonexistentSteps={true}
                    width={availableDimensions?.width ?? 1000}
                    height={availableDimensions?.height ?? 800}
                    actualSize={false}
                    controls={stableControls}
                    mediaPanelRef={modalData.mediaPanelRef}
                    config={stableConfig}
                    run={currentTileMediaSpec.run}
                    runSignature={currentTileMediaSpec.runSignature}
                    labels={labels}
                    globalStep={currentTileMediaSpec.mediaStep}
                    mediaKey={currentTileMediaSpec.mediaKey}
                    mediaIndex={currentTileMediaSpec.mediaIndex}
                    omitRunLabel={true}
                  />
                </S.FullscreenImageContainer>
              </S.FullscreenWrapper>
            </div>
            <div
              className="media-modal-footer flex flex-0 flex-col min-h-[0px]"
              data-test="media-modal-footer">
              <MediaBrowserModalFooter />
            </div>
          </Tailwind>
        </Content>
      </Portal>
    </Root>
  );
};

export default MediaBrowserFullscreenModal;
